import React, { useState, useEffect, useCallback } from 'react';
import { notificationAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Skeleton } from '../ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import {
  Bell, Plus, Trash2, Send, Eye, Users, Clock, Calendar, Play,
  CheckCircle, XCircle, RefreshCw, Mail, ChevronDown, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

const FREQ_LABELS = { daily: 'Daily', weekly: 'Weekly', biweekly: 'Bi-weekly', monthly: 'Monthly' };
const AUDIENCE_LABELS = { team_all: 'Entire Team', team_sales_reps: 'Sales Reps Only', product_managers: 'Product Managers Only', specific_users: 'Specific Users' };
const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const STATUS_COLORS = { success: 'bg-emerald-100 text-emerald-700', failed: 'bg-red-100 text-red-700', null: 'bg-slate-100 text-slate-500' };

export function NotificationConfigPage() {
  const [configs, setConfigs] = useState([]);
  const [types, setTypes] = useState({});
  const [team, setTeam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [testSendId, setTestSendId] = useState(null);
  const [testEmail, setTestEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [typesRes, configsRes, teamRes] = await Promise.all([
        notificationAPI.getTypes(),
        notificationAPI.getConfigs(),
        notificationAPI.getMyTeam(),
      ]);
      setTypes(typesRes.data.types || {});
      setConfigs(configsRes.data || []);
      setTeam(teamRes.data || null);
    } catch (e) {
      toast.error('Failed to load notification settings');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleToggle = async (id, enabled) => {
    try {
      await notificationAPI.updateConfig(id, { enabled });
      setConfigs(prev => prev.map(c => c.id === id ? { ...c, enabled } : c));
      toast.success(enabled ? 'Notification enabled' : 'Notification disabled');
    } catch { toast.error('Failed to update'); }
  };

  const handleDelete = async (id) => {
    try {
      await notificationAPI.deleteConfig(id);
      setConfigs(prev => prev.filter(c => c.id !== id));
      toast.success('Configuration deleted');
    } catch { toast.error('Failed to delete'); }
  };

  const handlePreview = async (id) => {
    try {
      const res = await notificationAPI.preview(id);
      setPreviewData(res.data);
      setShowPreview(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to generate preview');
    }
  };

  const handleTestSend = async () => {
    if (!testEmail || !testSendId) return;
    setSending(true);
    try {
      const res = await notificationAPI.testSend(testSendId, testEmail);
      if (res.data?.mail_result?.success) {
        toast.success(`Test email sent to ${testEmail}`);
      } else {
        toast.error(`Send failed: ${res.data?.mail_result?.reason || 'Unknown error'}`);
      }
      setTestSendId(null);
      setTestEmail('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send test');
    } finally { setSending(false); }
  };

  const handleExecute = async (id) => {
    try {
      const res = await notificationAPI.execute(id);
      if (res.data?.success) {
        toast.success(`Notification sent to ${res.data.recipients_count} recipients`);
        loadData();
      } else {
        toast.error(res.data?.error || 'Execution failed');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Execution failed');
    }
  };

  if (loading) return (
    <div className="space-y-4 p-2" data-testid="notification-config-loading">
      <Skeleton className="h-8 w-64" />
      <div className="grid gap-4">{[0,1,2].map(i => <Skeleton key={i} className="h-32 rounded-xl" />)}</div>
    </div>
  );

  const configuredTypes = new Set(configs.map(c => c.notification_type));

  return (
    <div className="space-y-5" data-testid="notification-config-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <Bell className="h-5 w-5 text-[#800000]" /> Notification Configuration
          </h1>
          <p className="text-sm text-gray-500">Configure automated reports and alerts for your team.</p>
        </div>
        <Button onClick={() => setShowCreate(true)} className="bg-[#800000] hover:bg-[#600000]" data-testid="create-notification-btn">
          <Plus className="h-4 w-4 mr-1.5" /> New Notification
        </Button>
      </div>

      {/* Team Summary */}
      {team && (
        <Card className="rounded-2xl border-slate-100">
          <CardContent className="p-4 flex items-center gap-4 flex-wrap">
            <Users className="h-5 w-5 text-[#800000]" />
            <div className="text-sm">
              <span className="font-medium text-slate-700">Your Team:</span>{' '}
              <span className="text-slate-500">{team.team?.length || 0} members</span>
              {team.roles?.sales_reps?.length > 0 && <Badge variant="outline" className="ml-2 text-[10px]">{team.roles.sales_reps.length} Sales Reps</Badge>}
              {team.roles?.product_managers?.length > 0 && <Badge variant="outline" className="ml-2 text-[10px]">{team.roles.product_managers.length} PMs</Badge>}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Active Configurations */}
      {configs.length === 0 ? (
        <Card className="rounded-2xl">
          <CardContent className="p-12 text-center">
            <Mail className="h-12 w-12 mx-auto mb-3 text-slate-200" />
            <p className="text-slate-500 font-medium">No notifications configured yet</p>
            <p className="text-xs text-slate-400 mt-1">Create your first automated notification to start keeping your team informed.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {configs.map(config => (
            <ConfigCard
              key={config.id}
              config={config}
              typeDef={types[config.notification_type]}
              expanded={expandedId === config.id}
              onToggleExpand={() => setExpandedId(expandedId === config.id ? null : config.id)}
              onToggle={(enabled) => handleToggle(config.id, enabled)}
              onDelete={() => handleDelete(config.id)}
              onPreview={() => handlePreview(config.id)}
              onTestSend={() => { setTestSendId(config.id); setTestEmail(''); }}
              onExecute={() => handleExecute(config.id)}
            />
          ))}
        </div>
      )}

      {/* Create Dialog */}
      <CreateNotificationDialog
        open={showCreate}
        onClose={() => setShowCreate(false)}
        types={types}
        configuredTypes={configuredTypes}
        team={team}
        onCreated={() => { setShowCreate(false); loadData(); }}
      />

      {/* Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-auto" data-testid="preview-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Eye className="h-5 w-5 text-[#800000]" /> Email Preview</DialogTitle>
            <DialogDescription>
              {previewData?.subject} — {previewData?.recipient_count} recipient(s)
            </DialogDescription>
          </DialogHeader>
          {previewData?.html && (
            <div className="border rounded-xl overflow-hidden mt-2">
              <iframe
                srcDoc={previewData.html}
                title="Email Preview"
                className="w-full min-h-[500px] border-0"
                sandbox="allow-same-origin"
                data-testid="preview-iframe"
              />
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Test Send Dialog */}
      <Dialog open={!!testSendId} onOpenChange={(open) => !open && setTestSendId(null)}>
        <DialogContent className="max-w-sm" data-testid="test-send-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Send className="h-5 w-5 text-[#800000]" /> Send Test Email</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Label>Recipient Email</Label>
            <Input value={testEmail} onChange={e => setTestEmail(e.target.value)} placeholder="you@example.com" data-testid="test-email-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTestSendId(null)}>Cancel</Button>
            <Button onClick={handleTestSend} disabled={sending || !testEmail} className="bg-[#800000] hover:bg-[#600000]" data-testid="send-test-btn">
              {sending ? <RefreshCw className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />}
              Send Test
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ConfigCard({ config, typeDef, expanded, onToggleExpand, onToggle, onDelete, onPreview, onTestSend, onExecute }) {
  const statusColor = STATUS_COLORS[config.last_run_status] || STATUS_COLORS[null];
  return (
    <Card className={`rounded-2xl transition-shadow ${config.enabled ? 'border-slate-200' : 'border-slate-100 opacity-70'}`} data-testid={`config-card-${config.id}`}>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0 cursor-pointer" onClick={onToggleExpand}>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-sm text-slate-800">{typeDef?.name || config.notification_type}</span>
              <Badge variant="outline" className="text-[10px]">{FREQ_LABELS[config.frequency] || config.frequency}</Badge>
              <Badge variant="outline" className="text-[10px]">{AUDIENCE_LABELS[config.audience] || config.audience}</Badge>
              {config.last_run_status && <Badge className={`text-[10px] ${statusColor}`}>{config.last_run_status === 'success' ? 'Last: OK' : 'Last: Failed'}</Badge>}
              {config.run_count > 0 && <span className="text-[10px] text-slate-400">Sent {config.run_count}x</span>}
            </div>
            <p className="text-xs text-slate-500 mt-1">{typeDef?.description}</p>
            <div className="flex items-center gap-3 mt-2 text-[10px] text-slate-400">
              <span className="flex items-center gap-0.5"><Calendar className="h-3 w-3" />{config.schedule?.day_of_week} at {config.schedule?.time_utc}</span>
              <span className="flex items-center gap-0.5"><Clock className="h-3 w-3" />{config.schedule?.timezone}</span>
              {config.last_run_at && <span>Last run: {new Date(config.last_run_at).toLocaleDateString()}</span>}
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Switch checked={config.enabled} onCheckedChange={onToggle} data-testid={`toggle-${config.id}`} />
            <ChevronDown className={`h-4 w-4 text-slate-400 cursor-pointer transition-transform ${expanded ? 'rotate-180' : ''}`} onClick={onToggleExpand} />
          </div>
        </div>

        {expanded && (
          <div className="border-t pt-3 mt-1 flex gap-2 flex-wrap">
            <Button size="sm" variant="outline" className="text-xs h-8" onClick={onPreview} data-testid={`preview-btn-${config.id}`}>
              <Eye className="h-3.5 w-3.5 mr-1" /> Preview Email
            </Button>
            <Button size="sm" variant="outline" className="text-xs h-8" onClick={onTestSend} data-testid={`test-send-btn-${config.id}`}>
              <Send className="h-3.5 w-3.5 mr-1" /> Test Send
            </Button>
            <Button size="sm" variant="outline" className="text-xs h-8 border-emerald-200 text-emerald-700 hover:bg-emerald-50" onClick={onExecute} data-testid={`execute-btn-${config.id}`}>
              <Play className="h-3.5 w-3.5 mr-1" /> Send Now
            </Button>
            <div className="flex-1" />
            <Button size="sm" variant="ghost" className="text-xs h-8 text-red-500 hover:text-red-700 hover:bg-red-50" onClick={onDelete} data-testid={`delete-btn-${config.id}`}>
              <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function CreateNotificationDialog({ open, onClose, types, configuredTypes, team, onCreated }) {
  const [form, setForm] = useState({
    notification_type: '',
    frequency: 'weekly',
    audience: 'team_all',
    day_of_week: 'monday',
    time_utc: '08:00',
    timezone: 'Asia/Muscat',
    email_subject: '',
  });
  const [creating, setCreating] = useState(false);

  const selectedType = types[form.notification_type] || null;
  const update = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  useEffect(() => {
    if (selectedType) {
      setForm(prev => ({
        ...prev,
        frequency: selectedType.default_frequency || 'weekly',
        audience: selectedType.default_audience || 'team_all',
        email_subject: selectedType.default_subject || '',
      }));
    }
  }, [form.notification_type, selectedType]);

  const handleCreate = async () => {
    if (!form.notification_type) return toast.error('Select a notification type');
    setCreating(true);
    try {
      await notificationAPI.createConfig(form);
      toast.success('Notification created');
      onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create');
    } finally { setCreating(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="create-notification-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Plus className="h-5 w-5 text-[#800000]" /> Create Notification</DialogTitle>
          <DialogDescription>Set up an automated report for your team</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Type Selection */}
          <div>
            <Label className="text-xs font-semibold text-slate-600 mb-1.5 block">Notification Type</Label>
            <div className="grid gap-2">
              {Object.entries(types).map(([key, def]) => {
                const already = configuredTypes.has(key);
                return (
                  <div
                    key={key}
                    onClick={() => !already && update('notification_type', key)}
                    className={`p-3 rounded-xl border cursor-pointer transition-all ${
                      form.notification_type === key ? 'border-[#800000] bg-[#800000]/5 ring-1 ring-[#800000]/20' :
                      already ? 'border-slate-100 bg-slate-50 opacity-50 cursor-not-allowed' :
                      'border-slate-200 hover:border-slate-300'
                    }`}
                    data-testid={`type-option-${key}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-slate-800">{def.name}</span>
                      {already && <Badge variant="outline" className="text-[9px]">Already configured</Badge>}
                    </div>
                    <p className="text-[11px] text-slate-500 mt-0.5">{def.description}</p>
                  </div>
                );
              })}
            </div>
          </div>

          {selectedType && (
            <>
              {/* Frequency + Schedule */}
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs font-semibold text-slate-600 mb-1.5 block">Frequency</Label>
                  <Select value={form.frequency} onValueChange={v => update('frequency', v)}>
                    <SelectTrigger className="h-9 text-sm" data-testid="frequency-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{(selectedType.allowed_frequencies || []).map(f => <SelectItem key={f} value={f}>{FREQ_LABELS[f]}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-semibold text-slate-600 mb-1.5 block">Day</Label>
                  <Select value={form.day_of_week} onValueChange={v => update('day_of_week', v)}>
                    <SelectTrigger className="h-9 text-sm" data-testid="day-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{DAYS.map(d => <SelectItem key={d} value={d}>{d.charAt(0).toUpperCase() + d.slice(1)}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-semibold text-slate-600 mb-1.5 block">Time (UTC)</Label>
                  <Input value={form.time_utc} onChange={e => update('time_utc', e.target.value)} className="h-9 text-sm" placeholder="08:00" data-testid="time-input" />
                </div>
              </div>

              {/* Audience */}
              <div>
                <Label className="text-xs font-semibold text-slate-600 mb-1.5 block">Audience</Label>
                <Select value={form.audience} onValueChange={v => update('audience', v)}>
                  <SelectTrigger className="h-9 text-sm" data-testid="audience-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(selectedType.allowed_audiences || []).map(a => (
                      <SelectItem key={a} value={a}>{AUDIENCE_LABELS[a] || a}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {team && (
                  <p className="text-[10px] text-slate-400 mt-1">
                    {form.audience === 'team_all' && `${team.team?.length || 0} team members`}
                    {form.audience === 'team_sales_reps' && `${team.roles?.sales_reps?.length || 0} sales reps`}
                    {form.audience === 'product_managers' && `${team.roles?.product_managers?.length || 0} product managers`}
                  </p>
                )}
              </div>

              {/* Subject */}
              <div>
                <Label className="text-xs font-semibold text-slate-600 mb-1.5 block">Email Subject</Label>
                <Input value={form.email_subject} onChange={e => update('email_subject', e.target.value)} className="h-9 text-sm" data-testid="subject-input" />
                <p className="text-[10px] text-slate-400 mt-0.5">Use {'{period}'} to include the date range.</p>
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleCreate} disabled={creating || !form.notification_type} className="bg-[#800000] hover:bg-[#600000]" data-testid="create-confirm-btn">
            {creating ? <RefreshCw className="h-4 w-4 mr-1 animate-spin" /> : <Bell className="h-4 w-4 mr-1" />}
            Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
